/** Header, tabbladen (Ideeën/Kaart/Game Over), menu-knop, footer. */
import { Lightbulb, Map as MapIcon, Menu, Trophy, type LucideIcon } from 'lucide-react';
import { lazy, Suspense, useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Footer } from '../components/Footer';
import { OfflineBanner } from '../components/OfflineBanner';
import type { TabId } from '../domain/types';
import IdeasTab from '../features/ideas/IdeasTab';
import MenuDrawer from '../features/menu/MenuDrawer';
import MilestoneWatcher from '../features/gameover/MilestoneWatcher';
import TimerBar from '../features/timer/TimerBar';
import { useTheme } from '../hooks/useTheme';
import { useTimer } from '../state/AppProviders';
import { KEYS } from '../storage/keys';
import { trackPage } from '../utils/analytics';

const TAB_TITLES: Record<TabId, string> = { ideeen: 'Ideeën', kaart: 'Kaart', gameover: 'Game Over' };

const MapTab = lazy(() => import('../features/map/MapTab'));
const GameOverTab = lazy(() => import('../features/gameover/GameOverTab'));

const TABS: { id: TabId; label: string; icon: LucideIcon }[] = [
  { id: 'ideeen', label: 'Ideeën', icon: Lightbulb },
  { id: 'kaart', label: 'Kaart', icon: MapIcon },
  { id: 'gameover', label: 'Game Over', icon: Trophy },
];

function parseTab(raw: string | null | undefined): TabId | null {
  const v = (raw ?? '').replace(/^#/, '').replace(/^tab-/, '').toLowerCase();
  return v === 'ideeen' || v === 'kaart' || v === 'gameover' ? v : null;
}

function readInitialTab(): TabId {
  const fromHash = parseTab(window.location.hash);
  if (fromHash) return fromHash;
  try {
    return parseTab(localStorage.getItem(KEYS.activeTab)) ?? 'ideeen';
  } catch {
    return 'ideeen';
  }
}

function TabList({ tab, onSelect, compact }: { tab: TabId; onSelect: (t: TabId, focus?: boolean) => void; compact: boolean }) {
  const onKeyDown = (e: KeyboardEvent) => {
    const i = TABS.findIndex((t) => t.id === tab);
    let next = -1;
    if (e.key === 'ArrowRight') next = (i + 1) % TABS.length;
    else if (e.key === 'ArrowLeft') next = (i - 1 + TABS.length) % TABS.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = TABS.length - 1;
    if (next < 0) return;
    e.preventDefault();
    onSelect(TABS[next].id, true);
  };

  return (
    <div
      role="tablist"
      aria-label="Hoofdschermen"
      onKeyDown={onKeyDown}
      className={compact ? 'flex items-center gap-1.5' : 'flex gap-1 rounded-2xl bg-slate-900/5 p-1 dark:bg-white/10'}
    >
      {TABS.map(({ id, label, icon: Icon }) => {
        const active = id === tab;
        return (
          <button
            key={id}
            id={`tab-${id}`}
            type="button"
            role="tab"
            aria-selected={active}
            aria-controls={`panel-${id}`}
            aria-label={compact ? label : undefined}
            title={compact ? label : undefined}
            tabIndex={active ? 0 : -1}
            onClick={() => onSelect(id)}
            className={`inline-flex min-h-11 items-center justify-center gap-2 whitespace-nowrap text-sm font-semibold transition-all duration-150 active:scale-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:focus-visible:outline-slate-100 ${
              compact ? 'size-11 rounded-full' : 'flex-1 rounded-xl px-3'
            } ${
              active
                ? 'bg-slate-900 text-slate-50 dark:bg-slate-100 dark:text-slate-900'
                : compact
                  ? 'bg-slate-900/5 text-slate-700 hover:bg-slate-900/10 dark:bg-white/10 dark:text-slate-200 dark:hover:bg-white/15'
                  : 'text-slate-700 hover:bg-slate-900/5 dark:text-slate-200 dark:hover:bg-white/10'
            }`}
          >
            <Icon size={compact ? 20 : 16} strokeWidth={2.2} aria-hidden="true" />
            {compact ? null : label}
          </button>
        );
      })}
    </div>
  );
}

export default function AppShell() {
  useTheme(); // past de `dark`-klasse en het achtergrondthema bij het opstarten toe
  const [tab, setTab] = useState<TabId>(readInitialTab);
  const [visited, setVisited] = useState<Set<TabId>>(() => new Set([tab]));
  const [menuOpen, setMenuOpen] = useState(false);
  const [compact, setCompact] = useState(false);
  const { timer } = useTimer();
  const pendingFocus = useRef(false);

  const select = useCallback((next: TabId, focus = false) => {
    pendingFocus.current = focus;
    setTab(next);
    setVisited((cur) => (cur.has(next) ? cur : new Set(cur).add(next)));
    try {
      localStorage.setItem(KEYS.activeTab, next);
    } catch {
      /* opslag niet beschikbaar */
    }
    // Hash bijwerken zonder ?share= te verliezen.
    const url = `${window.location.pathname}${window.location.search}#${next}`;
    if (window.location.hash !== `#${next}`) window.history.replaceState(null, '', url);
  }, []);

  // Elke schermwissel telt als paginaweergave in Google Analytics (alleen na toestemming).
  useEffect(() => {
    trackPage(`/${tab}`, TAB_TITLES[tab]);
  }, [tab]);

  // Deep links via hashchange.
  useEffect(() => {
    const onHash = () => {
      const t = parseTab(window.location.hash);
      if (t) {
        setTab(t);
        setVisited((cur) => (cur.has(t) ? cur : new Set(cur).add(t)));
      }
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // Focus mee met pijltjestoetsen.
  useEffect(() => {
    if (pendingFocus.current) {
      pendingFocus.current = false;
      document.getElementById(`tab-${tab}`)?.focus();
    }
  }, [tab, compact]);

  // Compact bij scrollen (met hysterese tegen flikkeren).
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setCompact((c) => (c ? y > 4 : y > 15));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const fallback = (
    <p className="py-16 text-center text-sm text-slate-600 dark:text-slate-300" role="status">
      Laden…
    </p>
  );

  const panel = (id: TabId, children: React.ReactNode) =>
    visited.has(id) ? (
      <div key={id} id={`panel-${id}`} role="tabpanel" aria-labelledby={`tab-${id}`} hidden={tab !== id} tabIndex={-1}>
        {children}
      </div>
    ) : null;

  return (
    <div className="min-h-dvh text-slate-900 dark:text-slate-100">
      <header className="sticky top-0 z-30 bg-slate-100/70 backdrop-blur-md dark:bg-slate-950/60">
        <div className="mx-auto flex max-w-2xl items-center gap-3 px-4 py-3">
          <img src="/GO-logo-tp_512x280.png" alt="" width={56} height={31} className="h-8 w-auto shrink-0" />
          <div className="min-w-0 flex-1">
            {/* Compact op een smalle telefoon: alleen het logo, de naam blijft voor schermlezers. */}
            <h1 className={`text-xl font-extrabold leading-tight ${compact ? 'max-[480px]:sr-only' : ''}`}>GameOn</h1>
            {/* In de compacte kop is geen ruimte naast de tabknoppen: tagline verbergen. */}
            {compact ? null : (
              <p className="truncate text-xs text-slate-600 dark:text-slate-300">Kies je missie &amp; ontdek activiteiten</p>
            )}
          </div>
          {compact ? <TabList tab={tab} onSelect={select} compact /> : null}
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-label="Menu openen"
            aria-haspopup="dialog"
            aria-expanded={menuOpen}
            className="inline-flex size-11 shrink-0 items-center justify-center rounded-full bg-slate-900/5 text-slate-700 transition-all duration-150 hover:bg-slate-900/10 active:scale-95 dark:bg-white/10 dark:text-slate-200 dark:hover:bg-white/15"
          >
            <Menu size={22} aria-hidden="true" />
          </button>
        </div>
        {compact ? null : (
          <div className="mx-auto max-w-2xl px-4 pb-3">
            <TabList tab={tab} onSelect={select} compact={false} />
          </div>
        )}
      </header>

      <main className={`mx-auto max-w-2xl px-4 pt-4 ${timer ? 'pb-48' : 'pb-24'}`}>
        {panel('ideeen', <IdeasTab />)}
        {panel('kaart', <Suspense fallback={fallback}><MapTab /></Suspense>)}
        {panel('gameover', <Suspense fallback={fallback}><GameOverTab /></Suspense>)}
        <Footer />
      </main>

      <TimerBar />
      <MilestoneWatcher />
      <OfflineBanner />
      <MenuDrawer open={menuOpen} onClose={() => setMenuOpen(false)} />
    </div>
  );
}
